import { formatDistanceToNow } from 'date-fns';
import { Check, ChevronLeft, ChevronRight, Database, MoreHorizontal, Search } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { SortableHeader } from '@/components/analytics/components/SortableHeader';
import { Loading, Spinner } from '@/components/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/NewButton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  FIRM_SYNC_PROVIDER_OPTIONS,
  FIRM_SYNC_STATUS_OPTIONS,
  FIRM_SYNCS_PAGE_SIZE,
  type FirmSortOrder,
  type FirmSyncItem,
  type FirmSyncSortField,
  useFirmSyncs,
} from '@/hooks/useFirmAdmin';
import { apiErrorMessage, backendClient } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { FilterSelect } from './FilterSelect';
import { FirmAdminEmptyState, FirmAdminShell } from './ShellTabs';

type DeletionMode = 'delete_files' | 'keep_files';

interface SyncDeletionInfo {
  file_count: number;
  provider_type: string;
}

function formatRelative(dateString: string | null): string {
  if (!dateString) return '—';
  try {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true });
  } catch {
    return '—';
  }
}

function statusClassName(status: string, isActive: boolean): string {
  if (!isActive || status === 'Inactive') {
    return 'text-slate-500';
  }
  if (status.startsWith('Sync Blocked') || status.includes('no longer exists')) {
    return 'text-crimson-600';
  }
  if (status.startsWith('Sync Paused')) {
    return 'text-amber-700';
  }
  return 'text-emerald-700';
}

export function SyncsTab() {
  const searchInputId = useId();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedProviders, setSelectedProviders] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [sortField, setSortField] = useState<FirmSyncSortField>('last_synced');
  const [sortOrder, setSortOrder] = useState<FirmSortOrder>('desc');
  const [page, setPage] = useState(1);
  const [syncToDelete, setSyncToDelete] = useState<FirmSyncItem | null>(null);
  const [deletionInfo, setDeletionInfo] = useState<SyncDeletionInfo | null>(null);
  const [deletionMode, setDeletionMode] = useState<DeletionMode>('keep_files');
  const [isLoadingDeletionInfo, setIsLoadingDeletionInfo] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const deletionRequestIdRef = useRef(0);

  const { data, isLoading, error, refresh } = useFirmSyncs({
    q: debouncedQuery,
    providers: selectedProviders,
    statuses: selectedStatuses,
    sortField,
    sortOrder,
    page,
    pageSize: FIRM_SYNCS_PAGE_SIZE,
  });
  const syncs = data?.syncs ?? [];
  const totalCount = data?.total_count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / FIRM_SYNCS_PAGE_SIZE));

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedQuery(query.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(handle);
  }, [query]);

  const handleSort = (field: FirmSyncSortField) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder(field === 'last_synced' ? 'desc' : 'asc');
    }
    setPage(1);
  };

  const openDeleteDialog = async (sync: FirmSyncItem) => {
    const requestId = ++deletionRequestIdRef.current;
    setSyncToDelete(sync);
    setDeletionInfo(null);
    setDeletionMode('keep_files');
    setIsLoadingDeletionInfo(true);
    try {
      const response = await backendClient.get<SyncDeletionInfo>(
        `/sync/folders/${sync.id}/deletion-info`,
      );
      if (requestId !== deletionRequestIdRef.current) return;
      setDeletionInfo(response.data);
    } catch (err) {
      if (requestId !== deletionRequestIdRef.current) return;
      toast.error(apiErrorMessage(err) || 'Failed to load sync deletion details');
      setSyncToDelete(null);
    } finally {
      if (requestId === deletionRequestIdRef.current) {
        setIsLoadingDeletionInfo(false);
      }
    }
  };

  const deleteSync = async () => {
    if (!syncToDelete || !deletionInfo) return;
    setIsDeleting(true);
    try {
      await backendClient.delete(`/sync/folders/${syncToDelete.id}`, {
        data: { deletion_mode: deletionMode },
      });
      toast.success('Sync deleted');
      setSyncToDelete(null);
      setDeletionInfo(null);
      await refresh();
    } catch (err) {
      toast.error(apiErrorMessage(err) || 'Failed to delete sync');
    } finally {
      setIsDeleting(false);
    }
  };

  const hasFilters = Boolean(query.trim())
    || selectedProviders.length > 0
    || selectedStatuses.length > 0;

  return (
    <FirmAdminShell
      title="Syncs"
      description="Document management connections across the firm."
    >
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-md">
          <label htmlFor={searchInputId} className="sr-only">Search syncs</label>
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            id={searchInputId}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search by matter name or number"
            className="h-10 pl-9"
          />
        </div>
        <FilterSelect
          label="Provider"
          allLabel="All providers"
          selectedNoun="providers"
          options={[...FIRM_SYNC_PROVIDER_OPTIONS]}
          selected={selectedProviders}
          onChange={(next) => {
            setSelectedProviders(next);
            setPage(1);
          }}
        />
        <FilterSelect
          label="Status"
          allLabel="All statuses"
          selectedNoun="statuses"
          options={[...FIRM_SYNC_STATUS_OPTIONS]}
          selected={selectedStatuses}
          onChange={(next) => {
            setSelectedStatuses(next);
            setPage(1);
          }}
        />
      </div>

      {isLoading ? (
        <div className="flex h-56 items-center justify-center">
          <Loading />
        </div>
      ) : error ? (
        <FirmAdminEmptyState
          icon={Database}
          title="Could not load syncs"
          description="Try refreshing the page. If this keeps happening, contact support."
        />
      ) : (
        <div>
          <div className="overflow-hidden rounded-md border bg-white">
            <Table className="table-fixed">
              <TableHeader>
                <TableRow>
                  <TableHead>Matter</TableHead>
                  <SortableHeader
                    label="Provider"
                    field="provider"
                    currentField={sortField}
                    currentOrder={sortOrder}
                    onSort={(field) => handleSort(field as FirmSyncSortField)}
                    width="10%"
                  />
                  <SortableHeader
                    label="Owner"
                    field="owner"
                    currentField={sortField}
                    currentOrder={sortOrder}
                    onSort={(field) => handleSort(field as FirmSyncSortField)}
                    width="15%"
                  />
                  <TableHead className="hidden lg:table-cell" style={{ width: '15%' }}>
                    Source
                  </TableHead>
                  <TableHead className="hidden xl:table-cell" style={{ width: '14%' }}>
                    Destination
                  </TableHead>
                  <TableHead style={{ width: '13%' }}>Status</TableHead>
                  <SortableHeader
                    label="Last synced"
                    field="last_synced"
                    currentField={sortField}
                    currentOrder={sortOrder}
                    onSort={(field) => handleSort(field as FirmSyncSortField)}
                    width="140px"
                  />
                  <TableHead className="w-14 pl-0 pr-2">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {syncs.length > 0 ? (
                  syncs.map((sync) => (
                    <TableRow key={sync.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-900">
                            {sync.matter_name}
                          </p>
                          {sync.matter_no ? (
                            <p className="text-xs text-muted-foreground">{sync.matter_no}</p>
                          ) : null}
                          {sync.case_reference && sync.case_reference !== sync.matter_no ? (
                            <p className="text-xs text-muted-foreground">{sync.case_reference}</p>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{sync.provider_label}</TableCell>
                      <TableCell>
                        <p className="truncate text-sm" title={sync.created_by_user_name ?? undefined}>
                          {sync.created_by_user_name || sync.created_by_user_email || '—'}
                        </p>
                        {sync.created_by_user_name && sync.created_by_user_email ? (
                          <p
                            className="truncate text-xs text-muted-foreground"
                            title={sync.created_by_user_email}
                          >
                            {sync.created_by_user_email}
                          </p>
                        ) : null}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <p className="truncate text-sm" title={sync.source_label ?? undefined}>
                          {sync.source_label || '—'}
                        </p>
                      </TableCell>
                      <TableCell className="hidden xl:table-cell">
                        <p className="truncate text-sm" title={sync.destination_label ?? undefined}>
                          {sync.destination_label || '—'}
                        </p>
                      </TableCell>
                      <TableCell>
                        <p className={cn('text-sm font-medium', statusClassName(sync.status, sync.is_active))}>
                          {sync.status}
                        </p>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatRelative(sync.last_synced_at)}
                      </TableCell>
                      <TableCell className="pl-0 pr-2">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={MoreHorizontal}
                              aria-label={`Actions for ${sync.matter_name} sync`}
                            />
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onSelect={() => {
                                void openDeleteDialog(sync);
                              }}
                            >
                              Delete sync
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} className="p-0">
                      <FirmAdminEmptyState
                        icon={Database}
                        title={hasFilters ? 'No matching syncs' : 'No syncs yet'}
                        description={
                          hasFilters
                            ? 'Try a different search or filter.'
                            : 'When matters connect document syncs, they will appear here.'
                        }
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <div className="mt-2 flex items-center justify-between px-2">
            <span className="text-sm text-muted-foreground">
              {totalCount === 0
                ? '0 syncs'
                : `Showing ${(page - 1) * FIRM_SYNCS_PAGE_SIZE + 1}–${Math.min(page * FIRM_SYNCS_PAGE_SIZE, totalCount)} of ${totalCount} syncs`}
            </span>
            {totalPages > 1 ? (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  icon={ChevronLeft}
                  aria-label="Previous page"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                />
                <Button
                  variant="outline"
                  size="sm"
                  icon={ChevronRight}
                  aria-label="Next page"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                />
              </div>
            ) : null}
          </div>
        </div>
      )}

      <Dialog
        open={!!syncToDelete}
        onOpenChange={(open) => {
          if (!open && !isDeleting) {
            deletionRequestIdRef.current += 1;
            setSyncToDelete(null);
            setDeletionInfo(null);
            setIsLoadingDeletionInfo(false);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete sync?</DialogTitle>
            <DialogDescription>
              {syncToDelete
                ? `Stop the ${syncToDelete.provider_label} sync for ${syncToDelete.matter_name}?`
                : null}
            </DialogDescription>
          </DialogHeader>

          {isLoadingDeletionInfo ? (
            <div className="flex h-24 items-center justify-center">
              <Spinner />
            </div>
          ) : deletionInfo?.provider_type === 'outlook' ? (
            <p className="text-sm text-muted-foreground">
              Synced emails will stay in Crimson, but automatic syncing from this folder will stop.
            </p>
          ) : deletionInfo ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                This sync contains {deletionInfo.file_count}{' '}
                {deletionInfo.file_count === 1 ? 'file' : 'files'}. Choose what happens to them.
              </p>
              <button
                type="button"
                onClick={() => setDeletionMode('keep_files')}
                className={cn(
                  'flex w-full items-center gap-3 rounded-md border px-4 py-3 text-left',
                  deletionMode === 'keep_files' ? 'border-slate-900 bg-slate-50' : 'border-slate-200',
                )}
              >
                <span className={cn(
                  'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2',
                  deletionMode === 'keep_files' && 'border-slate-900 bg-slate-900',
                )}>
                  {deletionMode === 'keep_files' ? <Check className="h-2.5 w-2.5 text-white" /> : null}
                </span>
                <span>
                  <span className="block text-sm font-medium">Keep files in Crimson</span>
                  <span className="block text-xs text-muted-foreground">
                    Files remain accessible, but syncing stops.
                  </span>
                </span>
              </button>
              <button
                type="button"
                onClick={() => setDeletionMode('delete_files')}
                className={cn(
                  'flex w-full items-center gap-3 rounded-md border px-4 py-3 text-left',
                  deletionMode === 'delete_files'
                    ? 'border-destructive bg-destructive/5'
                    : 'border-slate-200',
                )}
              >
                <span className={cn(
                  'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2',
                  deletionMode === 'delete_files' && 'border-destructive bg-destructive',
                )}>
                  {deletionMode === 'delete_files' ? <Check className="h-2.5 w-2.5 text-white" /> : null}
                </span>
                <span>
                  <span className="block text-sm font-medium">Delete all files</span>
                  <span className="block text-xs text-muted-foreground">
                    Permanently remove the synced files from Crimson.
                  </span>
                </span>
              </button>
            </div>
          ) : null}

          <DialogFooter>
            <Button
              variant="ghost"
              text="Cancel"
              disabled={isDeleting}
              onClick={() => setSyncToDelete(null)}
            />
            <Button
              variant={deletionMode === 'delete_files' ? 'destructive' : 'default'}
              text={deletionInfo?.provider_type === 'outlook' ? 'Continue' : 'Delete sync'}
              loadingText="Deleting"
              loading={isDeleting}
              disabled={!deletionInfo || isDeleting}
              onClick={() => {
                void deleteSync();
              }}
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </FirmAdminShell>
  );
}
